/**
 * Heartbeat de uso do CRM — regra pura (sem React, sem Supabase).
 *
 * Alimenta a "Saúde dos clubes" do admin com TEMPO ATIVO da equipe: um minuto
 * só conta quando a aba está visível E houve interação (clique, tecla, rolagem,
 * toque) no último minuto. Os minutos são agregados por módulo do CRM e
 * enviados a cada 5 min (e ao esconder/fechar a aba) pela RPC `track_activity`,
 * que soma em `club_user_activity_daily` (dia BRT, no servidor).
 *
 * Os módulos são uma allowlist ESPELHADA na RPC
 * (supabase/migrations/20261006190100_track_activity.sql): chave fora da lista
 * é descartada lá. Mudar aqui exige mudar lá.
 *
 * Doc: docs/funcionalidades/saude-dos-clubes.md
 */

export const ACTIVITY_MODULES = [
  "dashboard",
  "agenda",
  "quadras",
  "alunos",
  "aulas",
  "financeiro",
  "leads",
  "conversas",
  "pdv",
  "torneios",
  "configuracoes",
  "outros",
] as const;

export type ActivityModule = (typeof ACTIVITY_MODULES)[number];

/** Intervalo do relógio do heartbeat: um "minuto ativo" por tique. */
export const TICK_MS = 60_000;
/** Janela de interação que torna o minuto ativo. */
export const IDLE_WINDOW_MS = 60_000;
/** Envia a cada N tiques (5 min). */
export const FLUSH_EVERY_TICKS = 5;
/** Limites por chamada — iguais aos clamps da RPC. */
export const MAX_MINUTES_PER_FLUSH = 10;
export const MAX_PAGE_VIEWS_PER_FLUSH = 200;

/**
 * Prefixo de rota → módulo (rotas reais de apps/crm/src/App.tsx). O primeiro
 * prefixo que casa vence; "/" exato é o painel inicial.
 */
const ROUTE_MODULES: ReadonlyArray<readonly [string, ActivityModule]> = [
  ["/agenda", "agenda"],
  ["/quadras", "quadras"],
  ["/alunos", "alunos"],
  ["/membros", "alunos"],
  ["/experimentais", "alunos"],
  ["/aulas", "aulas"],
  ["/reposicoes", "aulas"],
  ["/professores", "aulas"],
  ["/professor/cadastros", "aulas"],
  ["/professor/financeiro-alunos", "financeiro"],
  ["/financeiro", "financeiro"],
  ["/faturas", "financeiro"],
  ["/planos", "financeiro"],
  ["/comissoes", "financeiro"],
  ["/pagamentos", "financeiro"],
  ["/despesas", "financeiro"],
  ["/notas-fiscais", "financeiro"],
  ["/indicadores", "financeiro"],
  ["/leads", "leads"],
  ["/oportunidades", "leads"],
  ["/whatsapp", "conversas"],
  ["/instagram", "conversas"],
  ["/pdv", "pdv"],
  ["/produtos", "pdv"],
  ["/torneios", "torneios"],
  ["/categorias", "torneios"],
  ["/configuracoes", "configuracoes"],
  ["/settings", "configuracoes"],
  ["/equipe", "configuracoes"],
  ["/professor/equipe", "configuracoes"],
  ["/minha-conta", "configuracoes"],
  ["/agregadores", "configuracoes"],
];

export function moduleFromPath(pathname: string): ActivityModule {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/") return "dashboard";
  for (const [prefix, mod] of ROUTE_MODULES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return mod;
  }
  return "outros";
}

export interface ActivityBuffer {
  minutes: number;
  pageViews: number;
  modules: Partial<Record<ActivityModule, number>>;
}

export function emptyBuffer(): ActivityBuffer {
  return { minutes: 0, pageViews: 0, modules: {} };
}

export function isEmptyBuffer(buf: ActivityBuffer): boolean {
  return buf.minutes === 0 && buf.pageViews === 0;
}

/** O minuto que acabou de passar conta como ativo? */
export function shouldCountMinute(input: {
  visible: boolean;
  lastInteractionAt: number;
  now: number;
}): boolean {
  return input.visible && input.now - input.lastInteractionAt <= IDLE_WINDOW_MS;
}

export function addMinute(buf: ActivityBuffer, mod: ActivityModule): ActivityBuffer {
  return {
    ...buf,
    minutes: buf.minutes + 1,
    modules: { ...buf.modules, [mod]: (buf.modules[mod] ?? 0) + 1 },
  };
}

export function addPageView(buf: ActivityBuffer): ActivityBuffer {
  return { ...buf, pageViews: buf.pageViews + 1 };
}

export interface TrackActivityPayload {
  p_club_id: string;
  p_active_minutes: number;
  p_page_views: number;
  p_modules: Partial<Record<ActivityModule, number>>;
}

/** Corpo da RPC, já nos limites que o servidor aceita. */
export function toTrackActivityPayload(buf: ActivityBuffer, clubId: string): TrackActivityPayload {
  const minutes = Math.min(buf.minutes, MAX_MINUTES_PER_FLUSH);
  const modules: Partial<Record<ActivityModule, number>> = {};
  for (const mod of ACTIVITY_MODULES) {
    const value = buf.modules[mod];
    if (value && value > 0) modules[mod] = Math.min(value, minutes);
  }
  return {
    p_club_id: clubId,
    p_active_minutes: minutes,
    p_page_views: Math.min(buf.pageViews, MAX_PAGE_VIEWS_PER_FLUSH),
    p_modules: modules,
  };
}

/**
 * Recusas de autorização que não são bug: sessão que acabou (logout, token
 * vencido com a aba escondida) cai como anon e a RPC devolve 42501; o
 * PostgREST devolve PGRST301 para JWT expirado. Telemetria perdida, não erro:
 * não vai ao Sentry.
 */
const IGNORABLE_TRACK_ERROR_CODES = new Set(["42501", "PGRST301"]);

export function isIgnorableTrackError(err: unknown): boolean {
  if (!err || typeof err !== "object" || !("code" in err)) return false;
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" && IGNORABLE_TRACK_ERROR_CODES.has(code);
}

/** Status HTTP equivalentes no envio por `fetch` keepalive (401/403). */
export function isIgnorableTrackStatus(status: number): boolean {
  return status === 401 || status === 403;
}

/**
 * Requisição do envio que sobrevive ao fechamento da aba: `fetch` com
 * `keepalive: true` direto no endpoint REST da RPC (o `supabase.rpc` não expõe
 * keepalive). Usado em `pagehide` e ao esconder a aba.
 */
export function buildKeepaliveTrackRequest(input: {
  supabaseUrl: string;
  apiKey: string;
  accessToken: string;
  payload: TrackActivityPayload;
}): { url: string; init: RequestInit } {
  return {
    url: `${input.supabaseUrl.replace(/\/+$/, "")}/rest/v1/rpc/track_activity`,
    init: {
      method: "POST",
      keepalive: true,
      headers: {
        apikey: input.apiKey,
        Authorization: `Bearer ${input.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input.payload),
    },
  };
}
